import { useEffect, useState } from "react";

import { TeamItem } from "../../types";

export type SectionTeamProps = {
  teamName: TeamItem["name"];
  teamPicture: TeamItem["picture"];
  displayTeamName?: boolean;
};

export const TeamLabel = ({
  teamName,
  teamPicture,
  displayTeamName = true,
}: SectionTeamProps) => {
  const [isImageValid, setIsImageValid] = useState(!!teamPicture);

  const isValidImage = (url: string): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      const img = new Image();
      img.src = url;

      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    }).catch(() => false);

  useEffect(() => {
    if (teamPicture) {
      isValidImage(teamPicture)
        .then(setIsImageValid)
        .catch(() => setIsImageValid(false));
    } else {
      setIsImageValid(false);
    }
  }, [teamPicture]);

  const getTeamInitials = (name: string) => {
    const words = name
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .split(/\s+/)
      .filter(Boolean);
    return words
      .slice(0, 3)
      .map((word) => word[0].toUpperCase())
      .join("");
  };

  return (
    <header className="flex flex-row flex-nowrap items-center rounded-small p-4 h-[7rem] font-poppins">
      {isImageValid ? (
        <img
          aria-label={`${teamName} Logo`}
          className="h-[5rem] print:h-[7.5rem]"
          src={teamPicture!}
        />
      ) : (
        <div
          className="flex h-[4rem] w-[4rem] items-center justify-center text-paragraph font-semibold rounded select-none bg-[radial-gradient(circle,_rgba(223,227,236,1)_100%,_rgba(191,199,217,1)_100%)] overflow-hidden"
          data-testid="team-initials"
        >
          {getTeamInitials(teamName)}
        </div>
      )}
      {displayTeamName && (
        <div
          data-testid="team-name"
          className="flex-1 text-paragraph font-semibold my-0 mx-4 whitespace-nowrap overflow-hidden overflow-ellipsis"
        >
          {teamName}
        </div>
      )}
    </header>
  );
};
